import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'Context/QueryClientContext';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import toastHandler from 'Utils/toastHandler';
import { OBRA_QUERY_KEY } from 'Data/keys/QueryKeys';
import { ObrasApiV2 } from 'Data/Obras.api.v2';
import {
  IObraDetailsRequest,
  IObraDetailsResponse,
  IObraDetailsUpdate,
} from 'Data/interfaces/Obra/IObraDetails';

export const useQueryUpdateDetailsObra = () => {
  const { mutate, isPending } = useMutation({
    mutationFn: (variables: IObraDetailsRequest) => {
      const request: IObraDetailsUpdate = {
        name: variables.Name,
        type: variables.Type,
        address: variables.Address,
        avatarFileName: variables.AvatarFileName,
      };
      return ObrasApiV2.updateDetails(variables.constructionStieId, request);
    },
    onMutate: async (variables) => {
      const previousObras = queryClient.getQueryData<IObraResponse[]>(
        [OBRA_QUERY_KEY],
      );
      const previousUniqueObra = queryClient.getQueryData<IObraResponse>(
        [OBRA_QUERY_KEY, Number(variables.constructionStieId)],
      );

      const response: IObraDetailsResponse = {
        constructionStieId: variables.constructionStieId,
        name: variables.Name,
        type: variables.Type,
        address: variables.Address,
        avatarFileName: variables.AvatarFileName,
      };

      queryClient.setQueryData<IObraResponse[]>([OBRA_QUERY_KEY], (
        oldData,
      ) => {
        return oldData?.map((obra) => {
          if (obra.ConstructionSiteId === response.constructionStieId) {
            return {
              ...obra,
              Name: response.name,
              Type: response.type,
              Address: response.address,
              AvatarFileName: response.avatarFileName,
            };
          }
          return obra;
        });
      });
      queryClient.setQueryData<IObraResponse>([OBRA_QUERY_KEY, Number(variables.constructionStieId)], (
        oldData,
      ) => {
        if (oldData && oldData.ConstructionSiteId === response.constructionStieId) {
          return {
            ...oldData,
            Name: response.name,
            Type: response.type,
            Address: response.address,
            AvatarFileName: response.avatarFileName,
          };
        }
        return oldData;
      });

      return { previousObras, previousUniqueObra };
    },
    onSuccess: () => {
      toastHandler.showSuccess('Dados da Obra atualizados com sucesso!');
    },
    onError: async (error: any, variables, context) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [OBRA_QUERY_KEY] }),
        queryClient.cancelQueries({
          queryKey: [OBRA_QUERY_KEY, Number(variables.constructionStieId)],
        }),
      ]);

      queryClient.setQueryData([OBRA_QUERY_KEY], context?.previousObras);
      queryClient.setQueryData(
        [OBRA_QUERY_KEY, Number(variables.constructionStieId)],
        context?.previousUniqueObra,
      );

      const errorMsg = error.errors
        ? error.errors[0].Message
        : 'Erro ao atualizar dados da Obra!';
      toastHandler.showError(errorMsg);
    },
  });

  return {
    updateDetailsObra: mutate,
    isLoading: isPending,
  };
};
